import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../lib/api';
import { speak, stopSpeaking, isSpeechSynthesisAvailable } from '../lib/speech';
import { isLessonCompleted } from '../lib/courseProgress';

/**
 * LessonPage —— 单课详情
 *
 * 这是 Midoo 式教学法的落地页，结构对应它的教学循环：
 *
 *   ① 本课要做什么（交际目标）
 *   ② 学会这几句就能做到（目标句型 + 粤拼 + 声调）
 *   ③ 现在就说（跟读练习 → 逐字声调纠音）
 *   ④ 真枪实弹用一次（场景对话，AI 扮演对方角色）
 *
 * ⚠️ 关键设计：第 ② ③ 步**完全不依赖云端密钥**。
 *    目标句型的粤拼是课程里手工标注的，发音评测用的是本地 Python 服务，
 *    所以即使 LLM / TTS 全部不可用，"学句子 → 跟读 → 拿到逐字纠音"这条链路依然成立。
 *    只有第 ④ 步（AI 对话）需要 LLM。
 */

const LEVEL_LABEL = {
  beginner: { name: '初级', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' },
  intermediate: { name: '中级', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300' },
  advanced: { name: '高级', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300' },
};

export default function LessonPage() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // 练后总结：由 ChatPage 在结束练习时算好并通过路由 state 带过来。
  // Midoo 式教学里，课程详情页不前置（不先给句型表），而是练完之后
  // 作为「复习页」再出现——此时学生已经有过实际使用的体验，
  // 再看句型、看自己的问题，吸收效率完全不同。
  const practiceReport = location.state?.report || null;
  const practiceCorrections = location.state?.corrections || [];

  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const [voiceNotice, setVoiceNotice] = useState(null);
  const [done] = useState(() => isLessonCompleted(lessonId));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/curriculum/${lessonId}`);
      const data = response.data?.data || response.data;
      setLesson(data);
    } catch (e) {
      console.error('[LessonPage] 加载课程失败:', e);
      setError(e?.response?.status === 404 ? '课程不存在' : (e?.message || '加载失败'));
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    load();
    return () => stopSpeaking();
  }, [load]);

  /**
   * 朗读一句粤语
   * 优先用浏览器粤语音色（离线可用）；拿到的音色类型会如实告知用户，
   * 因为用普通话音色读粤语字是不准的，不能让用户以为那就是标准发音。
   */
  const handleSpeak = async (phrase, index) => {
    setSpeakingIndex(index);
    setVoiceNotice(null);
    const result = await speak(phrase.cantonese, { rate: 0.85 });
    setSpeakingIndex(null);

    if (!result.spoken) {
      setVoiceNotice({ kind: 'error', text: '当前设备无法朗读，请对照粤拼自行朗读。' });
    } else if (result.kind === 'chinese') {
      setVoiceNotice({
        kind: 'warn',
        text: '当前设备没有粤语音色，正在用普通话音色代替，发音可能不准，建议以粤拼为准。',
      });
    }
  };

  /**
   * 进入本课的引导式课堂（分步教学）
   *
   * 原项目这里有两个出口：
   *   /practice —— 老的跟读打分页
   *   /chat     —— 自由场景对话页
   * 方言通只保留课程闭环，这两个页面都没有带过来。而课堂
   * （/session）本身就是「听示范 → 跟读 → 填空 → 换词 → 进场景对话 → 结算」，
   * 已经覆盖了原来两个出口要做的事，所以统一收敛到课堂，避免出现死链。
   */
  const handleStartClass = () => {
    navigate(`/session/${lesson.id}`);
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white dark:bg-zinc-900">
        <span className="material-icons !text-4xl animate-spin text-zinc-400">refresh</span>
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-white px-8 dark:bg-zinc-900">
        <span className="material-icons !text-5xl text-zinc-300">error_outline</span>
        <p className="text-center text-sm text-zinc-500 dark:text-zinc-400">{error || '课程不存在'}</p>
        <button
          type="button"
          onClick={() => navigate('/courses')}
          className="rounded-xl bg-teal-500 px-5 py-2.5 text-sm font-bold text-white active:opacity-80"
        >
          返回课程列表
        </button>
      </div>
    );
  }

  const levelInfo = LEVEL_LABEL[lesson.level] || LEVEL_LABEL.beginner;

  return (
    <div className="min-h-screen bg-slate-50 pb-32 dark:bg-zinc-900">
      {/* Header */}
      <header className="bg-gradient-to-br from-teal-500 to-sky-500 px-5 pt-12 pb-6 dark:from-teal-700 dark:to-sky-700">
        <div className="mb-3 flex items-center justify-between">
          <button type="button" onClick={() => navigate(-1)} className="text-white" aria-label="返回">
            <span className="material-icons !text-2xl">arrow_back</span>
          </button>
          <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${levelInfo.cls}`}>
            {levelInfo.name}
          </span>
        </div>
        <h1 className="text-xl font-bold text-white">
          {lesson.icon} {lesson.title}
        </h1>
        <p className="mt-1 flex items-center gap-3 text-[11px] text-white/80">
          <span className="flex items-center gap-0.5">
            <span className="material-icons !text-sm">schedule</span>
            {lesson.durationMin} 分钟
          </span>
          <span className="flex items-center gap-0.5">
            <span className="material-icons !text-sm">format_quote</span>
            {lesson.phrases.length} 个目标句型
          </span>
        </p>
      </header>

      <main className="px-5 py-5">
        {/* ⓪ 练后总结：只有从对话页结束练习过来时才有 */}
        {practiceReport && (
          <section className="mb-6 overflow-hidden rounded-2xl bg-white shadow-sm dark:bg-zinc-800">
            <div className="flex items-center justify-between bg-teal-500 px-4 py-3">
              <h2 className="flex items-center gap-1.5 text-sm font-bold text-white">
                <span className="material-icons !text-lg">insights</span>
                本次练习结果
              </h2>
              <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-bold text-white">
                {practiceReport.completedTasks}/{practiceReport.totalTasks}
              </span>
            </div>
            <div className="p-4">
              <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-200">
                {practiceReport.summary}
              </p>

              {practiceReport.completedLabels?.length > 0 && (
                <div className="mt-3">
                  <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-zinc-400">
                    已完成的任务
                  </p>
                  <ul className="space-y-1">
                    {practiceReport.completedLabels.map((label) => (
                      <li key={label} className="flex items-start gap-1.5 text-[13px] text-zinc-600 dark:text-zinc-300">
                        <span className="material-icons !text-base shrink-0 text-emerald-500">check_circle</span>
                        {label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {practiceReport.remainingLabels?.length > 0 && (
                <div className="mt-3">
                  <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-zinc-400">
                    还可以再练
                  </p>
                  <ul className="space-y-1">
                    {practiceReport.remainingLabels.map((label) => (
                      <li key={label} className="flex items-start gap-1.5 text-[13px] text-zinc-600 dark:text-zinc-300">
                        <span className="material-icons !text-base shrink-0 text-zinc-300 dark:text-zinc-600">
                          radio_button_unchecked
                        </span>
                        {label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {practiceReport.focus?.length > 0 && (
                <div className="mt-3 rounded-xl bg-amber-50 px-3 py-2.5 dark:bg-amber-900/25">
                  <p className="mb-1 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                    <span className="material-icons !text-sm">lightbulb</span>
                    下一步重点
                  </p>
                  <ul className="space-y-1">
                    {practiceReport.focus.map((f) => (
                      <li key={f} className="text-[13px] leading-relaxed text-amber-900 dark:text-amber-200">
                        · {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ① 交际目标 */}
        <section className="mb-6 rounded-2xl bg-white p-4 shadow-sm dark:bg-zinc-800">
          <h2 className="mb-1.5 flex items-center gap-1.5 text-sm font-bold text-zinc-800 dark:text-zinc-100">
            <span className="material-icons !text-lg text-teal-500">flag</span>
            学完你能
          </h2>
          <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">{lesson.goal}</p>
        </section>

        {/* ② 目标句型 */}
        <section className="mb-6">
          <h2 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-zinc-800 dark:text-zinc-100">
            <span className="material-icons !text-lg text-teal-500">format_quote</span>
            目标句型
            <span className="ml-1 text-[11px] font-normal text-zinc-400">点 🔊 听发音，点 🎤 跟读打分</span>
          </h2>

          {voiceNotice && (
            <div
              className={`mb-3 rounded-xl px-3 py-2 text-xs leading-relaxed ${
                voiceNotice.kind === 'warn'
                  ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                  : 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
              }`}
            >
              {voiceNotice.text}
            </div>
          )}

          <div className="space-y-3">
            {lesson.phrases.map((phrase, index) => (
              <div
                key={index}
                className="rounded-2xl border border-zinc-100 bg-white p-4 shadow-sm dark:border-zinc-700/60 dark:bg-zinc-800"
              >
                <p className="text-lg font-bold leading-relaxed text-zinc-800 dark:text-zinc-100">
                  {phrase.cantonese}
                </p>
                <p className="mt-1 font-mono text-xs text-teal-600 dark:text-teal-400">
                  {phrase.jyutping}
                </p>
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{phrase.mandarin}</p>

                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleSpeak(phrase, index)}
                    className="flex w-full items-center justify-center gap-1 rounded-xl bg-teal-50 py-2 text-xs font-bold text-teal-700 active:opacity-70 dark:bg-teal-900/40 dark:text-teal-300"
                  >
                    <span className="material-icons !text-base">volume_up</span>
                    听发音
                  </button>
                </div>
              </div>
            ))}
          </div>

          {!isSpeechSynthesisAvailable() && (
            <p className="mt-2 text-[11px] text-zinc-400">
              当前浏览器不支持语音合成，请对照粤拼朗读。
            </p>
          )}
        </section>

        {/* 关键生词 */}
        {lesson.vocab?.length > 0 && (
          <section className="mb-6">
            <h2 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-zinc-800 dark:text-zinc-100">
              <span className="material-icons !text-lg text-teal-500">bookmark</span>
              关键生词
            </h2>
            <div className="space-y-2">
              {lesson.vocab.map((v, i) => (
                <div
                  key={i}
                  className="flex items-baseline gap-3 rounded-xl bg-white px-4 py-2.5 shadow-sm dark:bg-zinc-800"
                >
                  <span className="font-bold text-zinc-800 dark:text-zinc-100">{v.word}</span>
                  <span className="font-mono text-[11px] text-teal-600 dark:text-teal-400">
                    {v.jyutping}
                  </span>
                  <span className="flex-1 text-right text-[11px] text-zinc-500 dark:text-zinc-400">
                    {v.meaning}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 文化贴士 */}
        {lesson.cultureTip && (
          <section className="mb-6 rounded-2xl bg-amber-50 p-4 dark:bg-amber-900/25">
            <h2 className="mb-1.5 flex items-center gap-1.5 text-sm font-bold text-amber-800 dark:text-amber-200">
              <span className="material-icons !text-lg">lightbulb</span>
              文化贴士
            </h2>
            <p className="text-xs leading-relaxed text-amber-800/90 dark:text-amber-200/90">
              {lesson.cultureTip}
            </p>
          </section>
        )}
      </main>

      {/* 底部主行动：进入引导式课堂（课程的唯一练习入口） */}
      <div
        className="fixed bottom-0 left-0 right-0 mx-auto max-w-md border-t border-zinc-200 bg-white/95 px-5 pt-4 backdrop-blur-xl dark:border-zinc-700 dark:bg-zinc-900/95"
        style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          onClick={handleStartClass}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-teal-500 to-sky-500 py-3.5 font-bold text-white transition-opacity active:opacity-80"
        >
          <span className="material-icons">school</span>
          {done ? '再上一遍这一课' : '开始上课'}
        </button>
        {done && (
          <p className="mt-1.5 text-center text-[11px] text-emerald-600 dark:text-emerald-400">
            ✓ 已完成本课
          </p>
        )}
      </div>
    </div>
  );
}
